import React, { useState } from 'react';
// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
// Custom components
import DefaultAuth from 'layouts/auth/Default';
// Assets
import { yupResolver } from '@hookform/resolvers/yup';
import illustration from 'assets/img/auth/auth.png';
import { useForm } from 'react-hook-form';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { UserLogin } from '../../../reactRedux/thunks/authThunk/authThunk';
import { signInValidationSchema } from '../../../utils/CommonValidation';

function SignIn() {
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const brandStars = useColorModeValue('secondary', 'brand.400');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);
  const {
    handleSubmit,
    reset,
    register,
    formState: { errors },
  } = useForm({ mode: 'all', resolver: yupResolver(signInValidationSchema) });

  const onSubmit = (data) => {
    dispatch(
      UserLogin(data, () => {
        navigate('/admin/dashboard');
        reset();
      }),
    );
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: '100%', md: 'max-content' }}
        w="100%"
        mx={{ base: 'auto', lg: '0px' }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: '30px', md: '60px' }}
        px={{ base: '25px', md: '0px' }}
        mt={{ base: '40px', md: '14vh' }}
        flexDirection="column"
      >
        <Box me="auto">
          <Heading
            color={textColor}
            fontSize="36px"
            mb="10px"
            fontFamily="Montserrat"
          >
            Sign In
          </Heading>
          <Text
            mb="36px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="md"
          >
            Enter your email and password to sign in!
          </Text>
        </Box>
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: '100%', md: '420px' }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          mb={{ base: '20px', md: 'auto' }}
        >
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <FormControl>
              <Box mb="24px">
                <FormLabel
                  display="flex"
                  ms="4px"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColor}
                  mb="8px"
                >
                  Email<Text color={brandStars}>*</Text>
                </FormLabel>
                <Input
                  isRequired={true}
                  variant="auth"
                  fontSize="sm"
                  ms={{ base: '0px', md: '0px' }}
                  type="email"
                  placeholder="mail@simmmple.com"
                  mb="2px"
                  fontWeight="500"
                  size="lg"
                  {...register('email')}
                  name="email"
                />
                {errors?.email && (
                  <p style={{ color: 'red', fontSize: '14px' }}>
                    {errors?.email?.message}
                  </p>
                )}
              </Box>
              <Box mb="24px">
                <FormLabel
                  ms="4px"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColor}
                  display="flex"
                >
                  Password<Text color={brandStars}>*</Text>
                </FormLabel>
                <InputGroup size="md">
                  <Input
                    isRequired={true}
                    fontSize="sm"
                    placeholder="Min. 8 characters"
                    mb="2px"
                    size="lg"
                    type={show ? 'text' : 'password'}
                    variant="auth"
                    {...register('password')}
                    name="password"
                  />
                  <InputRightElement
                    display="flex"
                    alignItems="center"
                    mt="4px"
                  >
                    <Icon
                      color={textColorSecondary}
                      _hover={{ cursor: 'pointer' }}
                      as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                      onClick={handleClick}
                    />
                  </InputRightElement>
                </InputGroup>
                {errors?.password && (
                  <p style={{ color: 'red', fontSize: '14px' }}>
                    {errors?.password?.message}
                  </p>
                )}
              </Box>
              <Button
                type="submit"
                fontSize="sm"
                variant="brand"
                fontWeight="500"
                bg="primary"
                color="white"
                _hover={{ bg: 'primary', opacity: 0.8 }}
                w="100%"
                h="50"
                mb="24px"
              >
                Sign In
              </Button>
              <Box textAlign="right" mb="5px">
                <Text
                  fontSize="sm"
                  color="blue.500"
                  _hover={{ textDecoration: 'underline' }}
                >
                  <Link to="/auth/forgot-password">Forgot Password?</Link>
                </Text>
              </Box>
            </FormControl>
          </form>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
